/**
 * Pesquisa de leads do CRM do Odoo (equipas Prospeção + Vendas) para os
 * assistentes solar, AC e BC — as leads do simulador vêm só do Odoo (D1).
 * Mostra equipa, etapa e etiquetas; as etiquetas sugerem o módulo. Ao
 * escolher, o pai preenche o cliente (`onSelect`) e grava `odoo_lead_id`.
 */
import { useEffect, useState } from "react"
import { Link2, Search, X } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { searchOdooLeads, type OdooLead, type SimModule } from "@/lib/api"

const MODULE_LABEL: Record<SimModule, string> = {
  solar: "Solar",
  ac: "Ar Condicionado",
  bc_aqs: "Bomba de calor AQS",
  bc_aq: "Bomba de calor aquecimento",
}

/** Campos do cliente que uma lead do Odoo preenche. */
export function clientFromOdooLead(l: OdooLead) {
  return {
    client_name: l.contact_name || l.partner?.name || l.name,
    client_email: l.email ?? "",
    client_phone: l.phone ?? "",
    address: l.street ?? "",
    city: l.city ?? "",
    postal_code: l.zip ?? "",
  }
}

export interface OdooLeadSearchProps {
  shell: AppShell | null
  /** Módulo do assistente — avisa quando as etiquetas da lead apontam para outro. */
  module: SimModule
  /** Lead ligada (id + nome), ou nenhuma. */
  linked: { id: number; name: string | null } | null
  onSelect: (lead: OdooLead) => void
  onClear: () => void
}

export function OdooLeadSearch({ shell, module, linked, onSelect, onClear }: OdooLeadSearchProps) {
  const [query, setQuery] = useState("")
  const [debounced, setDebounced] = useState("")
  const [results, setResults] = useState<OdooLead[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hint, setHint] = useState<SimModule | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    if (!shell || debounced.trim().length < 2) { setResults([]); setError(null); return }
    let cancelled = false
    setSearching(true)
    searchOdooLeads(shell, debounced.trim())
      .then((rows) => { if (!cancelled) { setResults(rows); setError(null) } })
      .catch((e) => { if (!cancelled) { setResults([]); setError(e instanceof Error ? e.message : "Falha a pesquisar no Odoo") } })
      .finally(() => { if (!cancelled) setSearching(false) })
    return () => { cancelled = true }
  }, [shell, debounced])

  function pick(l: OdooLead) {
    onSelect(l)
    setHint(l.module_hint && l.module_hint !== module ? l.module_hint : null)
    setQuery("")
    setResults([])
  }

  return (
    <div className="space-y-1">
      <Label>Procurar lead no Odoo</Label>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder="Nome, email, telefone ou cliente…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {results.length > 0 && (
        <div className="mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-sm">
          {results.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => pick(l)}
              className="flex w-full flex-col items-start gap-1 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent/10"
            >
              <div className="flex w-full items-center gap-2">
                <span className="font-medium">{l.name}</span>
                {l.contact_name && l.contact_name !== l.name && (
                  <span className="text-xs text-muted-foreground">· {l.contact_name}</span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {[l.team?.name, l.stage?.name].filter(Boolean).join(" · ")}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {[l.email, l.phone, l.city, l.salesperson].filter(Boolean).join(" · ") || "sem contacto"}
              </div>
              {l.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {l.tags.map((t) => (
                    <Badge key={t} variant="outline" className="px-1.5 py-0 text-[10px]">{t}</Badge>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {searching && <p className="text-xs text-muted-foreground">A procurar no Odoo…</p>}
      {!searching && error && <p className="text-xs text-destructive">{error}</p>}
      {!searching && !error && debounced.trim().length >= 2 && results.length === 0 && query === debounced && (
        <p className="text-xs text-muted-foreground">Nenhuma lead da Prospeção ou Vendas com «{debounced.trim()}».</p>
      )}
      {linked && (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-accent/10 px-3 py-2 text-sm">
          <Link2 className="h-4 w-4 text-accent" />
          <span>
            Lead do Odoo: <strong>{linked.name ?? `#${linked.id}`}</strong>
          </span>
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
            onClick={() => { setHint(null); onClear() }}
          >
            <X className="h-3 w-3" /> desligar
          </button>
        </div>
      )}
      {linked && hint && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          As etiquetas desta lead indicam {MODULE_LABEL[hint]} — confirma que é o assistente certo.
        </p>
      )}
    </div>
  )
}
